import { enableDirectApi, getApiBaseUrl, PRODUCTION_API_BASE } from '../config/api.config';

const CHECK_CACHE_MS = 30_000;

let cached: { ok: boolean; checkedAt: number } | null = null;
let inflight: Promise<boolean> | null = null;

function healthUrl(): string {
  return `${getApiBaseUrl().replace(/\/$/, '')}/health`;
}

/** Lightweight API reachability check (uses /health, not site-info). */
export async function checkApiHealth(force = false): Promise<boolean> {
  if (!force && cached && Date.now() - cached.checkedAt < CHECK_CACHE_MS) {
    return cached.ok;
  }

  if (!force && inflight) {
    return inflight;
  }

  inflight = (async () => {
    try {
      const res = await fetch(healthUrl(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (res.ok) {
        cached = { ok: true, checkedAt: Date.now() };
        return true;
      }
      if (getApiBaseUrl().startsWith('/')) {
        const direct = await fetch(`${PRODUCTION_API_BASE.replace(/\/$/, '')}/health`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (direct.ok) {
          enableDirectApi();
        }
        const ok = direct.ok;
        cached = { ok, checkedAt: Date.now() };
        return ok;
      }
      cached = { ok: false, checkedAt: Date.now() };
      return false;
    } catch {
      if (getApiBaseUrl().startsWith('/')) {
        try {
          const direct = await fetch(`${PRODUCTION_API_BASE.replace(/\/$/, '')}/health`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          if (direct.ok) {
            enableDirectApi();
          }
          const ok = direct.ok;
          cached = { ok, checkedAt: Date.now() };
          return ok;
        } catch {
          // fall through
        }
      }
      cached = { ok: false, checkedAt: Date.now() };
      return false;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Pages that remain usable when the API is temporarily unreachable. */
export function isOfflineTolerantPath(pathname: string): boolean {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path.startsWith('/pay')) return true;
  if (path === '/pos') return true;
  if (path === '/guide' || path === '/documentation') return true;
  if (['/', '/features', '/pricing', '/about', '/contact', '/apps', '/blog'].includes(path)) return true;
  if (path.startsWith('/apps/')) return true;
  if (path.startsWith('/page/') || /^\/(ar|zh|ko)\/page\//.test(path) || path.startsWith('/blog')) return true;
  return false;
}

export function clearApiHealthCache(): void {
  cached = null;
  inflight = null;
}
